export function formatLocalDateLabelFromYMD(ymd) {
  try {
    if (!ymd || typeof ymd !== 'string' || ymd.length < 10) return '';
    // Construct a Date in local time at noon to avoid timezone boundary issues
    const d = new Date(ymd + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch (_) {
    return '';
  }
}

// Returns YYYY-MM-DD for a Date interpreted in local time
export function toLocalYMD(dateLike) {
  try {
    if (!dateLike) return '';
    // If input begins with YYYY-MM-DD, treat it as a calendar date (ignore timezone/time)
    if (typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateLike)) {
      const head = dateLike.slice(0, 10);
      const [y, m, day] = head.split('-').map((s) => parseInt(s, 10));
      const dLocal = new Date(y, m - 1, day); // local
      const yy = dLocal.getFullYear();
      const mm = String(dLocal.getMonth() + 1).padStart(2, '0');
      const dd = String(dLocal.getDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    }
    const d = new Date(dateLike);
    // Build YMD from local components to avoid UTC shifting
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch (_) {
    return '';
  }
}

// Formats an ISO/date into a full local label, safe from off-by-one
export function formatLocalFullDateLabel(dateLike) {
  try {
    const ymd = toLocalYMD(dateLike);
    if (!ymd) return '';
    // Reuse noon trick for display stability
    const d = new Date(ymd + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {
    return '';
  }
}

// Returns a Date object constructed from a date-like or YMD string,
// normalized to local time by using the YMD-at-noon pattern.
export function localDateFrom(dateLike) {
  try {
    const ymd = toLocalYMD(dateLike);
    if (!ymd) return null;
    return new Date(ymd + 'T12:00:00');
  } catch (_) {
    return null;
  }
}

// Generic local-safe formatter: builds a local Date from the input and formats with options.
export function formatLocalDate(dateLike, options = {}, locale = 'en-US') {
  try {
    const d = localDateFrom(dateLike);
    if (!d) return '';
    return d.toLocaleDateString(locale, options);
  } catch (_) {
    return '';
  }
}


// --- Local datetime helpers for inputs and ISO conversions ---

// Returns 'YYYY-MM-DDTHH:MM' for a datetime-local input, using local time
export function toLocalDateTimeInput(dateLike) {
  try {
    let d;
    if (dateLike instanceof Date && !Number.isNaN(dateLike.getTime())) {
      d = dateLike;
    } else if (dateLike) {
      d = localDateFrom(dateLike) || new Date();
    } else {
      d = new Date();
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}`;
  } catch (_) {
    return '';
  }
}

/** API ISO → datetime-local string in the user's local timezone */
export function isoToLocalDateTimeInput(iso) {
  try {
    if (!iso) return '';
    return toLocalDateTimeInput(new Date(iso));
  } catch (_) {
    return '';
  }
}

/** Shift a datetime-local value by hours/minutes (local wall time). */
export function addToLocalDateTimeInput(datetimeLocal, { hours = 0, minutes = 0 } = {}) {
  try {
    const iso = fromLocalDateTimeInputToISO(datetimeLocal);
    if (!iso) return '';
    const d = new Date(iso);
    d.setHours(d.getHours() + hours, d.getMinutes() + minutes, 0, 0);
    return toLocalDateTimeInput(d);
  } catch (_) {
    return '';
  }
}

export function isLocalDateTimeBefore(a, b) {
  try {
    const aIso = fromLocalDateTimeInputToISO(a);
    const bIso = fromLocalDateTimeInputToISO(b);
    if (!aIso || !bIso) return false;
    return new Date(aIso).getTime() < new Date(bIso).getTime();
  } catch (_) {
    return false;
  }
}

// Converts an HTML datetime-local string 'YYYY-MM-DDTHH:MM' to ISO string (UTC),
// preserving the same local wall time semantics
export function fromLocalDateTimeInputToISO(datetimeLocal) {
  try {
    if (!datetimeLocal || typeof datetimeLocal !== 'string') return null;
    // Build a Date from local components
    const [datePart, timePart] = datetimeLocal.split('T');
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split('-').map((s) => parseInt(s, 10));
    const [hh, mm] = timePart.split(':').map((s) => parseInt(s, 10));
    const local = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    return local.toISOString();
  } catch (_) {
    return null;
  }
}

// Return ISO string for start of the provided local YMD (00:00:00 local)
export function startOfLocalDayISO(ymd) {
  try {
    const head = typeof ymd === 'string' ? ymd.slice(0, 10) : toLocalYMD(ymd);
    if (!head) return null;
    const [y, m, d] = head.split('-').map((s) => parseInt(s, 10));
    const local = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
    return local.toISOString();
  } catch (_) {
    return null;
  }
}

// All-day bounds are calendar dates (UTC midnight), not the viewer's local midnight.
export function startOfAllDayISO(ymd) {
  const head = typeof ymd === 'string' ? ymd.slice(0, 10) : toLocalYMD(ymd);
  return head ? `${head}T00:00:00.000Z` : null;
}

export function endOfAllDayISO(ymd) {
  const head = typeof ymd === 'string' ? ymd.slice(0, 10) : toLocalYMD(ymd);
  return head ? `${head}T23:59:59.000Z` : null;
}

// Return ISO string for end of the provided local YMD (23:59:59 local)
export function endOfLocalDayISO(ymd) {
  try {
    const head = typeof ymd === 'string' ? ymd.slice(0, 10) : toLocalYMD(ymd);
    if (!head) return null;
    const [y, m, d] = head.split('-').map((s) => parseInt(s, 10));
    const local = new Date(y, (m || 1) - 1, d || 1, 23, 59, 59, 0);
    return local.toISOString();
  } catch (_) {
    return null;
  }
}


